import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  capitalize,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

import { TableComponent } from 'commons/components/Table';
import { BinType } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import { getSTOPickItemBinRecommendationInfo } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setSTOPickedItem } from 'redux-stores/reducers/stoPickItemReducer';
import { AppDispatch } from 'redux-stores/store';
import { STO_PICK_ROUTES } from 'routes/constants';

import { STOPickingProcessForm } from './components';
import { config } from './recommendationTable.config';
import S from './STOPickItemPage.style';

export const STOPickItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { state } = useLocation();
  const pickIdx = state && state.pickIdx >= 0 ? state.pickIdx : -999;
  const { assignedSTOItems, stoPickedItem, binRecommendationInfo } =
    useSelector((state: RootReducerInterface) => state.stoPickItem);

  /** ******** STATES ********* */
  const [recommendationBinType, setRecommendationBinType] =
    useState<BinType | null>(BinType.RETAIL);

  const [pickedProgress, setPickedProgress] = useState(0);
  const [colorProgress, setColorProgress] = useState(colors.PRIMARY_RED);
  const [isAllBasketVerified, setIsAllBasketVerified] = useState(false);

  const isFirstPage = useMemo(() => {
    return pickIdx <= 0;
  }, [pickIdx]);
  const isLastPage = useMemo(() => {
    return pickIdx >= assignedSTOItems.length - 1;
  }, [pickIdx, assignedSTOItems.length]);

  /** ******** USE EFFECTS ********* */
  useEffect(() => {
    if (assignedSTOItems.length > 0 && pickIdx >= 0) {
      dispatch(setSTOPickedItem(assignedSTOItems[pickIdx]));

      // set default recommendation info to RETAIL
      dispatch(
        getSTOPickItemBinRecommendationInfo({
          skuCode: assignedSTOItems[pickIdx].sku_code,
          binType: BinType.RETAIL,
        }),
      );
      setRecommendationBinType(BinType.RETAIL);
    } else if (pickIdx < 0) {
      navigate(STO_PICK_ROUTES.LIST);
    }

    // unmount component
    return () => {
      dispatch(setSTOPickedItem(null));
    };
  }, [dispatch, assignedSTOItems, pickIdx]);

  useEffect(() => {
    if (stoPickedItem) {
      // check basket verified and quantity progress
      let quantity = 0;
      let _isAllBasketVerified = true;
      stoPickedItem.pick_actual.forEach((actual) => {
        quantity += actual.quantity;
        /* 
          check if all basket is verified. 
          if quantity is 0, treat it as verified 
          as it means the basket is not yet filled and no item is picked, 
          so no need to verify the basket yet 
        */
        _isAllBasketVerified =
          _isAllBasketVerified && (actual.is_verified || actual.quantity === 0);
      });
      const color =
        quantity === 0
          ? colors.PRIMARY_RED
          : quantity === stoPickedItem.quantity
          ? colors.PRIMARY_GREEN
          : colors.PRIMARY_YELLOW;
      setColorProgress(color);
      setPickedProgress(quantity);
      setIsAllBasketVerified(_isAllBasketVerified);
    }
  }, [dispatch, stoPickedItem]);

  // early return process if stoPickedItem is null
  // to handle case page being refreshed
  if (!assignedSTOItems || assignedSTOItems.length === 0 || pickIdx < 0) {
    navigate(STO_PICK_ROUTES.LIST);
    return <div />;
  }

  if (!stoPickedItem) return <div />;

  /** ******** FUNCTIONS ********* */
  const getSourceBinTableData = (value: BinType): void => {
    dispatch(
      getSTOPickItemBinRecommendationInfo({
        skuCode: stoPickedItem.sku_code,
        binType: value,
      }),
    );
    setRecommendationBinType(value);
  };

  const goToPrevSKUHandler = (): void => {
    if (isFirstPage) {
      navigate(STO_PICK_ROUTES.LIST);
      return;
    }
    const newIdx = pickIdx - 1;
    navigate(STO_PICK_ROUTES.ITEM, { state: { pickIdx: newIdx } });
  };

  const goToNextSKUHandler = (): void => {
    if (isLastPage) {
      navigate(STO_PICK_ROUTES.LIST);
      return;
    }
    const newIdx = pickIdx + 1;
    navigate(STO_PICK_ROUTES.ITEM, { state: { pickIdx: newIdx } });
  };

  return (
    <div>
      {/* Content Section */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="STOPickTextItemSKU">
            {stoPickedItem.sku_code}
          </S.Subtitle>
          <S.Subtitle item xs={12} data-testid="STOPickTextItemProduct">
            {stoPickedItem.name}
          </S.Subtitle>
          <S.UOMSubtitle item xs={12} data-testid="STOPickTextItemUom">
            {stoPickedItem.uom}
          </S.UOMSubtitle>
        </S.SubtitleWrapper>
        <S.HeaderContentWrapper container>
          <Grid item xs={6}>
            <S.ProgressText data-testid="STOPickTextItemOrderQty">
              TOTAL ORDER QTY: {stoPickedItem.quantity}
            </S.ProgressText>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'right' }}>
            <S.ProgressText>
              ORDER PICKED:
              <S.ProgressSpan
                color={colorProgress}
                data-testid="STOPickTextItemOrderPicked"
              >
                {pickedProgress}/{stoPickedItem.quantity}
              </S.ProgressSpan>
            </S.ProgressText>
          </Grid>
        </S.HeaderContentWrapper>
        <S.HeaderTableWrapper container>
          <Grid item xs={6} sm={3} md={2}>
            <S.SubHeaderText>Recommend Bins</S.SubHeaderText>
          </Grid>
          <Grid item xs={6} sm={9} md={10}>
            <FormControl
              sx={{
                '.MuiInputLabel-root.Mui-focused': {
                  display: 'none', // Hide label on hover
                },
              }}
            >
              {!recommendationBinType && (
                <InputLabel sx={{ transition: 'none', fontSize: 14 }}>
                  Select Bin Type
                </InputLabel>
              )}
              <S.SelectBinType
                value={recommendationBinType || ''}
                onChange={(e: SelectChangeEvent) =>
                  getSourceBinTableData(e.target.value as BinType)
                }
                inputProps={{
                  'data-testid': 'SelectSTTaskSourceBinType',
                }}
              >
                {Object.values(BinType).map((_binType) => (
                  <MenuItem value={_binType}>
                    {capitalize(_binType.replace('_', ' '))}
                  </MenuItem>
                ))}
              </S.SelectBinType>
            </FormControl>
          </Grid>
          <TableComponent
            data={binRecommendationInfo}
            config={config}
            additionalTableConfig={{ bodyFontSize: '12px' }}
          />
        </S.HeaderTableWrapper>

        {/* Content Section */}
        <STOPickingProcessForm
          stoItem={stoPickedItem}
          itemIdx={pickIdx}
          setIsAllBasketVerified={setIsAllBasketVerified}
        />
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer Section */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={goToPrevSKUHandler}
              disabled={!isAllBasketVerified}
              data-testid="STOPickBtnItemPrev"
            >
              {isFirstPage ? 'BACK TO PICK LIST' : 'PREV SKU'}
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              onClick={goToNextSKUHandler}
              disabled={!isAllBasketVerified}
              data-testid="STOPickBtnNext"
            >
              {isLastPage ? 'DONE' : 'NEXT SKU'}{' '}
              {isAllBasketVerified ? '✔' : '✘'}
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </div>
  );
};
